import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { HttpBaseService } from './http-base.service';
import { ObjectDataSourceDto, ObjectDefinitionCreateDto, ObjectDefinitionDetailDto, ObjectDefinitionListDto, ObjectDefinitionUpdateDto, ObjectIngestionDto } from '../dtos/object-definition.types';

@Injectable({
    providedIn: 'root'
})
export class ObjectDefinitionService extends HttpBaseService {
    private readonly baseEndpoint = 'api/ObjectDefinitions';

    /**
     * Get all object definitions
     */
    getAllObjectDefinitions(): Observable<ObjectDefinitionListDto[]> {
        return this.get<ObjectDefinitionListDto[]>(this.baseEndpoint);
    }

    /**
     * Get object definitions by thematic
     */
    getObjectDefinitionsByThematic(thematicId: string): Observable<ObjectDefinitionListDto[]> {
        if (!thematicId) {
            return of([]);
        }
        return this.get<ObjectDefinitionListDto[]>(`${this.baseEndpoint}/by-thematic/${thematicId}`);
    }

    /**
     * Get object definitions detail by id
     */
    getObjectDefinitionById(objectId: string): Observable<ObjectDefinitionDetailDto> {
        return this.get<ObjectDefinitionDetailDto>(`${this.baseEndpoint}/${objectId}`);
    }

    /**
     * Get object definitions by sector
     */
    getObjectDefinitionsByTopSector(sectorId: string): Observable<ObjectDefinitionListDto[]> {
        if (!sectorId) {
            return of([]);
        }
        return this.get<ObjectDefinitionListDto[]>(`${this.baseEndpoint}/by-top-sector/${sectorId}`);
    }

    /**
     * Get object definitions by sub sector
     */
    getObjectDefinitionsBySubSector(sectorId: string): Observable<ObjectDefinitionListDto[]> {
        if (!sectorId) {
            return of([]);
        }
        return this.get<ObjectDefinitionListDto[]>(`${this.baseEndpoint}/by-sub-sector/${sectorId}`);
    }

    /**
     * Get object definition for update
     */
    getObjectDefinitionForUpdate(objectId: string): Observable<ObjectDefinitionUpdateDto> {
        return this.get<ObjectDefinitionUpdateDto>(`${this.baseEndpoint}/${objectId}/update`);
    }

    /**
     * Create a new object definition
     * @returns Observable containing the created object definition's ID
     */
    createObjectDefinition(objectDefinition: ObjectDefinitionCreateDto): Observable<{ id: string }> {
        return this.post<{ id: string }>(this.baseEndpoint, objectDefinition);
    }

    /**
     * Update an existing object definition
     */
    updateObjectDefinition(objectDefinition: ObjectDefinitionUpdateDto): Observable<void> {
        return this.put<void>(this.baseEndpoint, objectDefinition);
    }

    /**
     * Delete an object definition
     */
    deleteObjectDefinition(objectId: string): Observable<void> {
        return this.delete<void>(`${this.baseEndpoint}/${objectId}`);
    }

    checkCodeExists(code: string): Observable<boolean> {
        return this.get<{ exists: boolean }>(`${this.baseEndpoint}/check-code-exists/${code}`)
            .pipe(map(response => response.exists));
    }

    getIngestions(): Observable<ObjectIngestionDto[]> {
        return this.get<ObjectIngestionDto[]>(`${this.baseEndpoint}/ingestions`);
    }

    /**
     * Get all object data sources
     * @returns Observable containing the list of object data sources
     */
    getDataSources(): Observable<ObjectDataSourceDto[]> {
        return this.get<ObjectDataSourceDto[]>(`${this.baseEndpoint}/data-sources`);
    }

    /**
     * Get object data source by ID
     * @param id - The ID of the data source
     * @returns Observable containing the object data source
     */
    getDataSourceById(id: string): Observable<ObjectDataSourceDto> {
        return this.get<ObjectDataSourceDto>(`${this.baseEndpoint}/data-sources/${id}`);
    }

    getDataSourceByCode(code: string): Observable<ObjectDataSourceDto> {
        return this.get<ObjectDataSourceDto>(`${this.baseEndpoint}/data-sources/by-code/${code}`);
    }

}